import { booleanAttribute } from "#ce/utils/props";
import { list as listProps } from "#ce/components/SpList/props";
import { props as searchBoxProps } from "#ce/components/SpSearchBox/props.js";
import { props as textFieldProps } from "#ce/components/SpTextField/props.js";

export const appearanceOptions = ["default", "inline"];

export const navigationOptions = ["drilldown", "flat", "accordion"];

export const accordionCaretVariantOptions = ["chevron", "disclosure"];

// Remove unused props from listProps
const listPropsFiltered = Object.fromEntries(
  Object.entries(listProps).filter(([key]) => key !== "subheader" && key !== "itemProps"),
);

/**
 * Panel-only prop subset used by the inner `Panel` component. The full
 * top-level `props` export (further down in this file) is a superset that adds
 * menu, activator, and navigation related props the outer `<sp-quantum-select>`
 * needs as well.
 */
export const panelProps = Object.freeze({
  ...listPropsFiltered,
  /**
   * name attribute used for the element's form value — required to participate in a native form
   * submit. Each selected item is submitted as its own entry under this name, the way a native
   * `<select multiple>` does.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Removes the ability to interact with the picker, and cascades from an ancestor
   * `<fieldset disabled>`.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Message the browser shows when a required picker is submitted with nothing selected. Names
   * what this particular picker is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * The selectable items, as an array of strings or objects. See
   * [Item data structure](#item-data-structure) in the docs for the full item shape and how each
   * field drives list behaviour.
   *
   * @type {Array}
   * @default []
   */
  items: { ...listProps.items },
  clearable: searchBoxProps.clearable,
  loading: textFieldProps.loading,
  placeholder: searchBoxProps.placeholder,
  /**
   * Icon name shown next to the currently selected item in the list.
   *
   * @type {String}
   * @default "check-filled"
   */
  selectedPrependIcon: {
    ...listProps.selectedPrependIcon,
    default: "check-filled",
  },
  /**
   * Fill colour for the `selectedPrependIcon` SVG.
   *
   * @type {String}
   * @default "var(--sp-comp-quantum-select-selected-prepend-icon-fill-color, var(--sp-sys-color-success))"
   */
  selectedPrependIconFillColor: {
    ...listProps.selectedPrependIconFillColor,
    default: "var(--sp-comp-quantum-select-selected-prepend-icon-fill-color, var(--sp-sys-color-success))",
  },

  /**
   * Current search query used to filter items.
   *
   * @type {String}
   * @default undefined
   */
  search: {
    type: String,
    default: undefined,
  },
  /**
   * Maximum number of matched items rendered for a search query. Undefined (the
   * default) renders every match. Set this when binding a large item set so a
   * broad query can't render thousands of rows synchronously and block the main
   * thread. Idle (no-query) browsing is unaffected.
   *
   * @type {Number|String}
   * @default undefined
   */
  maxResults: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Switch the in-list query filter from substring matching to typo-tolerant
   * fuzzy matching with relevance ordering ("kardiology" finds "Cardiology"; an
   * ordered-word pass ranks a progressive name match — "Brian Ab" → "Brian W.
   * Ablitz" — first). Fuzzy results render as a flat best-first list — section
   * headers/grouping are dropped while a query is active, since relevance order
   * makes section order meaningless.
   *
   * @type {Boolean|String}
   * @default false
   */
  fuzzySearch: booleanAttribute(),
  /**
   * While a query is active, visually mark the first selectable result as the
   * combobox's pending choice (the sp-list-item `highlighted` hint). Pair with
   * the exposed `firstResult()` method to commit that row on Enter from an
   * external activator field. Purely visual — implies no selection and never
   * moves focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  highlightFirstResult: booleanAttribute(),
  /**
   * Hide the search-bar input at the top of the list.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideSearchBar: booleanAttribute(),
  /**
   * Hide the "no data" message shown when no items match.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideNoData: booleanAttribute(),
  /**
   * Text shown when no items match the current search.
   *
   * @type {String}
   * @default "No data"
   */
  noDataText: {
    type: String,
    default: "No data",
  },
  /**
   * Sets a `data-testid` attribute on every rendered item row. A per-item
   * `props["data-testid"]` wins over this shared value. Pass a catalog value
   * from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  itemTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the panel's search input. Pass a catalog
   * value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: searchBoxProps.inputTestId,
  /**
   * Make branch rows (items with children) selectable in their own right: a
   * click on the row body selects the branch value, while a dedicated append
   * control toggles its children. Off (the default) keeps the classic accordion
   * behaviour where the whole branch row toggles expansion and is not selectable.
   *
   * @type {Boolean|String}
   * @default false
   */
  selectableBranches: booleanAttribute(),
  /**
   * Accessible label for the branch disclosure toggle, used only when
   * `selectableBranches` is set. Supply a translated string; the control's
   * `aria-expanded` conveys open/closed state.
   *
   * @type {String}
   * @default undefined
   */
  branchToggleLabel: {
    type: String,
    default: undefined,
  },
});

// Panel props the container binds explicitly (so v-bind="forwardablePanelProps(props)"
// must not overwrite them).
const CONTAINER_BOUND_PANEL_PROPS = ["items", "search", "maxResults", "fuzzySearch", "highlightFirstResult"];

/**
 * Subset of `panelProps` a container should forward to its inner Panel: every
 * panel prop except the ones the container consumes itself (`items`, `search`,
 * `maxResults`). Undefined values are skipped so Panel's own defaults still apply.
 */
export function forwardablePanelProps(props) {
  return Object.keys(panelProps).reduce((acc, key) => {
    if (CONTAINER_BOUND_PANEL_PROPS.includes(key)) {
      return acc;
    }
    const value = props[key];
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  ...panelProps,

  /**
   * Whether the dropdown menu is open.
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * Title shown in the navigation bar when navigating nested items.
   *
   * @type {String}
   * @default undefined
   */
  title: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the menu always visible (inline / embedded mode, no popup wrapper).
   *
   * @type {Boolean|String}
   * @default false
   */
  permanent: booleanAttribute(),
  /**
   * Maximum height of the dropdown menu.
   *
   * @type {String}
   * @default undefined
   */
  maxHeight: {
    type: String,
    default: undefined,
  },
  /**
   * Disable client-side filtering when searching. Use when data is filtered server-side and the host updates `items` directly.
   *
   * @type {Boolean|String}
   * @default false
   */
  noFilter: booleanAttribute(),
  /**
   * Show a pointer arrow on the popup menu, anchored at the activator.
   *
   * @type {Boolean|String}
   * @default false
   */
  pointer: booleanAttribute(),
  /**
   * Expand the popup to fill the available height on mobile.
   *
   * @type {Boolean|String}
   * @default false
   */
  mobileExpandHeightMax: booleanAttribute(),
  /**
   * Re-show the in-list search bar when the menu renders as a bottom sheet
   * (mobile), even if `hideSearchBar` is set. Use this when the host hides the
   * search bar because a slotted activator doubles as the search input on
   * desktop: in sheet mode that activator is covered by the sheet, so the user
   * needs the in-list search bar back to filter. No effect on desktop, in
   * `permanent`/`noMenu` mode, or when `hideSearchBar` is not set.
   *
   * @type {Boolean|String}
   * @default false
   */
  showSearchBarInSheet: booleanAttribute(),
  /**
   * Remove the menu wrapper and render the content inline without a popup or activator slot.
   *
   * @type {Boolean|String}
   * @default false
   */
  noMenu: booleanAttribute(),
  /**
   * Async loader for an item's children. Called with the item the user drilled into and must return a Promise that resolves to the children array. The navigation container manages back navigation internally, so no parent callback is needed.
   *
   * @type {Function}
   * @default undefined
   */
  loadChildren: {
    type: Function,
    required: false,
    default: undefined,
  },
  /**
   * Position of the popup menu (for example "top", "bottom", "start", "end"), forwarded to the underlying `sp-menu`.
   *
   * @type {String}
   * @default undefined
   */
  location: {
    type: String,
    default: undefined,
  },
  /**
   * Hide the title bar in the drilldown navbar.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideTitleBar: booleanAttribute(),
  /**
   * Close the dropdown menu automatically after an item is selected.
   *
   * @type {Boolean|String}
   * @default false
   */
  closeOnSelect: booleanAttribute(),
  /**
   * Close the menu when clicking inside the content area.
   *
   * @type {Boolean|String}
   * @default false
   */
  closeOnContentClick: booleanAttribute(),
  /**
   * Listen for keyboard navigation even when the menu is closed. The menu always listens while open; set to `true` to keep the host activator keyboard-driven when the menu is collapsed.
   *
   * @type {Boolean|String}
   * @default false
   */
  listenOnKeyEvent: booleanAttribute(),
  /**
   * Visual appearance. "default" is the standard floating dropdown; "inline" sizes the dropdown to the activator width with a tightened navbar, so it reads like a native select rendered inline (suited to inline or table contexts).
   *
   * @type {String}
   * @default "default"
   * @values default, inline
   * @storybook-control select
   */
  appearance: {
    type: String,
    default: "default",
    validator: (value) => appearanceOptions.includes(value),
  },
  /**
   * Discard the container's navigation state every time the menu opens, so the user always lands on the root frame.
   *
   * @type {Boolean|String}
   * @default false
   */
  startOnRoot: booleanAttribute(),
  /**
   * Navigation pattern used to render and traverse the items. "drilldown" shows
   * one level at a time with drill-in / back navigation; "flat" shows a single,
   * always visible list with optional search; "accordion" shows a single list
   * where top-level rows with children expand inline (single level only).
   *
   * @type {String}
   * @default "drilldown"
   * @values drilldown, flat, accordion
   * @storybook-control select
   */
  navigation: {
    type: String,
    default: "drilldown",
    validator: (value) => navigationOptions.includes(value),
  },
  /**
   * Glyph on accordion branch rows: "chevron" renders the rotating chevron caret;
   * "disclosure" renders a plus / minus toggle icon. Only applies when
   * `navigation="accordion"`.
   *
   * @type {String}
   * @default "chevron"
   * @values chevron, disclosure
   * @storybook-control select
   */
  accordionCaretVariant: {
    type: String,
    default: "chevron",
    validator: (value) => accordionCaretVariantOptions.includes(value),
  },
  /**
   * Ancestor items to pre-drill into when the picker opens, top-most first. Lets a host open the
   * picker already at depth (for example on a preselected nested item) while the container keeps
   * owning navigation; back navigation then works through the normal stack. Empty means open at root.
   *
   * @type {Array}
   * @default []
   */
  initialPath: { type: Array, default: () => [] },
});
